/** Merchant estimate. All money inputs and outputs are integer BRL centavos.
 * Never alters the cart, checkout, order, or Shopify tax settings.
 */
export function calculateImport({ goods, shipping = 0, currency, state, rates, dutiesIncluded, taxesIncluded, pricesExcludeTax = false, dutyRate = 60, dutyThreshold = 0 }) {
  if (currency !== 'BRL') return { status: 'currency' };
  if (!Number.isSafeInteger(goods) || goods < 0 || goods > 1e12) return { status: 'invalid' };
  if (goods === 0) return { status: 'empty' };
  if (!pricesExcludeTax && (dutiesIncluded || taxesIncluded)) return { status: 'included' };
  if (shipping === null) return { status: 'shipping' };
  if (!Number.isSafeInteger(shipping) || shipping < 0 || shipping > 1e12) return { status: 'invalid' };
  const cif = goods + shipping;
  const rate = Number(rates?.[state]);
  if (!state || !Number.isFinite(rate) || rate < 17 || rate > 20) return { status: 'state' };
  if (!Number.isFinite(dutyRate) || dutyRate < 0 || dutyRate > 100 || !Number.isSafeInteger(dutyThreshold) || dutyThreshold < 0) return { status: 'invalid' };
  const appliedDutyRate = cif >= dutyThreshold ? dutyRate : 0;
  const ii = Math.round(cif * appliedDutyRate / 100);
  const icms = Math.round((cif + ii) * rate / (100 - rate));
  return { status: 'ready', ii, icms, total: cif + ii + icms, rate, appliedDutyRate };
}

const shippingState = { state: '', zip: '', key: '', rates: [], selected: 0, status: 'idle', version: 0 };
try { shippingState.zip = sessionStorage.getItem('brazilbuy-import-zip') || ''; } catch { /* Storage is optional. */ }
const notifyShipping = () => document.dispatchEvent(new Event('brazilbuy:shipping'));
const formatMoney = cents => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
export async function fetchShippingRates({ root, state, zip, signal, request = fetch, pause = ms => new Promise(resolve => setTimeout(resolve, ms)) }) {
  const params = new URLSearchParams({ 'shipping_address[country]': 'Brazil', 'shipping_address[province]': state, 'shipping_address[zip]': zip });
  const init = await request(root + 'cart/prepare_shipping_rates.json?' + params, { method: 'POST', signal, credentials: 'same-origin' });
  if (!init.ok) throw new Error('quote');
  for (let i = 0; i < 10; i++) {
    const response = await request(root + 'cart/async_shipping_rates.json?' + params, { signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error('quote');
    const result = await response.json();
    if (result !== null) {
      if (!Array.isArray(result.shipping_rates)) throw new Error('quote');
      return result.shipping_rates.map(rate => {
        const raw = String(rate.price);
        if (!/^\d+(\.\d{1,2})?$/.test(raw) || (rate.currency && rate.currency !== 'BRL')) throw new Error('currency');
        const cents = Math.round(Number(raw) * 100);
        if (!Number.isSafeInteger(cents) || cents > 1e12) throw new Error('quote');
        return { name: rate.presentment_name || rate.name, cents };
      }).sort((a,b) => a.cents-b.cents);
    }
    await pause(800);
  }
  throw new Error('timeout');
}

if (typeof customElements !== 'undefined' && !customElements.get('brazilbuy-import-cost')) {
  class BrazilbuyImportCost extends HTMLElement {
    static observedAttributes = ['data-goods', 'data-currency', 'data-duties-included', 'data-taxes-included', 'data-rates', 'data-prices-exclude-tax', 'data-cart-key', 'data-duty-rate', 'data-usd-brl'];
    connectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.select = this.querySelector('[data-import-state]');
      if (!this.select) return;
      try { shippingState.state ||= sessionStorage.getItem('brazilbuy-import-state') || ''; } catch { /* Storage is optional. */ }
      this.select.value = shippingState.state;
      this.zip = this.querySelector('[data-shipping-zip]');
      this.quoteButton = this.querySelector('[data-shipping-calculate]');
      this.methods = this.querySelector('[data-shipping-method]');
      if (this.zip) this.zip.value = shippingState.zip;
      this.zip?.addEventListener('input', () => {
        shippingState.zip = this.zip.value;
        try { sessionStorage.setItem('brazilbuy-import-zip', shippingState.zip); } catch { /* Storage is optional. */ }
        this.invalidateShipping();
      }, { signal });
      this.quoteButton?.addEventListener('click', () => this.quoteShipping(), { signal });
      this.methods?.addEventListener('change', () => { shippingState.selected = Number(this.methods.value); notifyShipping(); }, { signal });
      document.addEventListener('brazilbuy:shipping', () => this.render(), { signal });
      this.select.addEventListener('change', () => {
        shippingState.state = this.select.value;
        this.invalidateShipping();
        try { sessionStorage.setItem('brazilbuy-import-state', this.select.value); } catch { /* Storage is optional. */ }
        document.dispatchEvent(new CustomEvent('brazilbuy:import-state', { detail: this.select.value }));
      }, { signal });
      document.addEventListener('brazilbuy:import-state', (event) => {
        this.select.value = event.detail;
        this.render();
      }, { signal });
      // Shopify section morphing may replace child nodes without reconnecting the host.
      this.observer = new MutationObserver(() => {
        const select = this.querySelector('[data-import-state]');
        if (select !== this.select || this.querySelector('[data-shipping-zip]') !== this.zip || this.querySelector('[data-shipping-calculate]') !== this.quoteButton || this.querySelector('[data-shipping-method]') !== this.methods) { this.observer.disconnect(); this.connectedCallback(); }
        else this.render();
      });
      this.observer.observe(this, { childList: true, subtree: true });
      this.render();
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
    attributeChangedCallback() { if (this.isConnected && this.select) this.render(); }
    invalidateShipping() {
      shippingState.version++;
      shippingState.rates = [];
      shippingState.status = 'idle';
      notifyShipping();
    }
    async quoteShipping() {
      const zip = (this.zip?.value || '').replace(/\D/g, '');
      if (!this.select.value || zip.length !== 8) {
        shippingState.status = 'address'; notifyShipping(); return;
      }
      this.shippingController?.abort();
      this.shippingController = new AbortController();
      const version = ++shippingState.version;
      shippingState.status = 'loading'; shippingState.rates = [];
      shippingState.state = this.select.value; shippingState.zip = zip;
      try { sessionStorage.setItem('brazilbuy-import-zip', zip); } catch { /* Storage is optional. */ }
      notifyShipping();
      try {
        const rates = await fetchShippingRates({ root: window.Shopify?.routes?.root || '/', state: this.select.value, zip, signal: this.shippingController.signal });
        if (version !== shippingState.version) return;
        shippingState.rates = rates; shippingState.selected = 0;
        shippingState.status = rates.length ? 'ready' : 'unavailable';
      } catch (error) {
        if (version !== shippingState.version) return;
        shippingState.status = 'error';
      }
      notifyShipping();
    }
    render() {
      // Shopify can morph option nodes while retaining the select itself.
      if (this.select.value !== shippingState.state) this.select.value = shippingState.state;
      const key = this.dataset.cartKey || this.dataset.goods;
      if (shippingState.key !== key) {
        shippingState.key = key; shippingState.version++; shippingState.rates = []; shippingState.status = 'idle';
        clearTimeout(this.refreshTimer);
        const version = shippingState.version;
        // Quantity and section updates arrive together. Quote once after the final cart revision.
        if (this.select.value && shippingState.zip.replace(/\D/g, '').length === 8) {
          this.refreshTimer = setTimeout(() => {
            if (this.isConnected && version === shippingState.version && shippingState.status === 'idle') this.quoteShipping();
          }, 250);
        }
      }
      if (this.zip && document.activeElement !== this.zip && this.zip.value !== shippingState.zip) this.zip.value = shippingState.zip;
      const shipping = shippingState.status === 'ready' && shippingState.state === this.select.value ? shippingState.rates[shippingState.selected]?.cents ?? null : null;
      if (this.quoteButton) this.quoteButton.disabled = shippingState.status === 'loading';
      const signature = JSON.stringify(shippingState.rates);
      if (this.methods && this.methods.dataset.signature !== signature) {
        this.methods.replaceChildren(...shippingState.rates.map((r,i) => new Option(r.name + ' — ' + formatMoney(r.cents), String(i))));
        this.methods.dataset.signature = signature;
      }
      if (this.methods) { this.methods.hidden = shippingState.rates.length === 0; if (this.methods.value !== String(shippingState.selected) && shippingState.rates.length) this.methods.value = String(shippingState.selected); }

      let rates = {};
      try { rates = JSON.parse(this.dataset.rates || '{}'); } catch { /* Invalid merchant config stays uncalculated. */ }
      const goods = Number(this.dataset.goods);
      const dutyRate = Number(this.dataset.dutyRate ?? 60);
      const usdBrl = Number(this.dataset.usdBrl);
      const dutyThreshold = this.dataset.usdBrl === undefined ? 0 : Number.isFinite(usdBrl) && usdBrl > 0 ? Math.round(50 * usdBrl * 100) : NaN;
      const result = calculateImport({ goods, shipping, currency: this.dataset.currency, state: this.select.value,
        rates, dutyRate, dutyThreshold, dutiesIncluded: this.dataset.dutiesIncluded === 'true', taxesIncluded: this.dataset.taxesIncluded === 'true', pricesExcludeTax: this.dataset.pricesExcludeTax === 'true' });
      const money = (cents) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
      const set = (key, value) => { const el = this.querySelector(`[data-import-${key}]`); if (el && el.textContent !== value) el.textContent = value; };
      const ready = result.status === 'ready';
      const included = result.status === 'included';
      set('shipping', shipping === null ? 'A calcular' : money(shipping));
      const shippingMessages = { idle: 'Informe estado e CEP para consultar o frete e calcular os tributos.', address: 'Selecione o estado e informe um CEP de 8 dígitos.', loading: 'Consultando opções de entrega…', ready: 'Frete consultado nas regras da loja. A opção e o endereço serão confirmados no checkout.', unavailable: 'Não há entrega disponível para este endereço. Confira o CEP e o estado.', error: 'Não foi possível consultar o frete. Tente novamente.' };
      set('shipping-message', shippingMessages[shippingState.status]);
      set('ii', ready ? money(result.ii) : included && this.dataset.dutiesIncluded === 'true' ? 'Incluído no preço' : 'A confirmar');
      set('icms', ready ? money(result.icms) : included && this.dataset.taxesIncluded === 'true' ? 'Incluído no preço' : result.status === 'state' ? 'Selecione o estado' : 'A confirmar');
      set('total', ready ? money(result.total) : 'A confirmar');
      set('rate', ready ? ` (${result.rate}%)` : '');
      set('ii-formula', ready ? `${money(goods + (shipping ?? 0))} × ${result.appliedDutyRate}% = ${money(result.ii)}` : `II = CIF × ${dutyRate}% a partir do limite informado.`);
      set('threshold', Number.isFinite(dutyThreshold) ? money(dutyThreshold) : 'A confirmar');
      set('icms-formula', ready ? `(${money(goods + (shipping ?? 0))} + ${money(result.ii)}) ÷ (1 − ${result.rate}%) × ${result.rate}% = ${money(result.icms)}` : 'ICMS = (CIF + II) ÷ (1 − alíquota do estado) × alíquota do estado.');
      const messages = {
        shipping: 'Os tributos e o total serão calculados após consultar o frete para seu endereço.',
        ready: 'Estimativa comercial conforme parâmetros da loja; não representa apuração fiscal oficial. Confira os valores cobrados no checkout e os tributos devidos na importação.',
        state: 'Selecione o estado de entrega para estimar ICMS e custo total. A estimativa considera o valor dos produtos após descontos.',
        included: 'O preço deste carrinho já inclui tributos ou direitos de importação. Confira o detalhamento no checkout; não somamos novamente esses valores.',
        currency: 'A estimativa está disponível para valores em reais (BRL). Confira os tributos e o total no checkout.',
        invalid: 'Não foi possível estimar este valor. Confira os tributos e o total no checkout.',
        empty: 'Adicione um produto para consultar a estimativa.'
      };
      set('message', messages[result.status]);
      this.dataset.status = result.status;
    }
  }
  customElements.define('brazilbuy-import-cost', BrazilbuyImportCost);
}

